import * as React from 'react';
import { Tab } from '@headlessui/react';
import { PersonalDetails } from './PersonalDetails';
import { LoaderScreen, Typography } from '@/components/common';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Option } from '@/interfaces';
import AppRoutes from '@/const/routes';
import { useQuery } from '@tanstack/react-query';
import { GET_USER_ME, userService } from '@/api';
import { PaymentDetails } from './PaymentDetails';
import { CredentialsSettings } from './CredentialsSettings';
import { NotificationPreferences } from './NotificationPreferences';
import { ClientCommunicationPreferences } from './ClientCommunicationPreferences';

export const SettingsScreen: React.FC = () => {
	const [selectedIndex, setSelectedIndex] = React.useState<number>();
	const [options, setOptions] = React.useState<Option[]>();
	const router = useRouter();
	const { tab } = router.query;

	const { data: userInformation, refetch } = useQuery({
		queryKey: [GET_USER_ME],
		queryFn: () => userService.getUserMe(),
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		if (!userInformation?.data) return;

		const commonOptions = [];

		if (userInformation && userInformation.data.role === 'client') {
			commonOptions.push(
				{
					label: 'Personal Information',
					name: 'personal',
					href: AppRoutes.CLIENTAPP_SETTINGS_PERSONAL,
				},
				{
					label: 'Account Access',
					name: 'credentials',
					href: AppRoutes.CLIENTAPP_SETTINGS_CREDENTIALS,
				},
				{
					label: 'Payment Details',
					name: 'payment',
					href: AppRoutes.CLIENTAPP_SETTINGS_PAYMENT,
				},
				{
					label: 'Communication Preferences',
					name: 'notifications',
					href: AppRoutes.CLIENTAPP_SETTINGS_NOTIFICATIONS,
				}
			);
		} else if (userInformation && userInformation.data.role === 'lawyer') {
			commonOptions.push(
				{
					label: 'Personal Information',
					name: 'personal',
					href: AppRoutes.LAWYERAPP_SETTINGS_PERSONAL,
				},
				{
					label: 'Account Access',
					name: 'credentials',
					href: AppRoutes.LAWYERAPP_SETTINGS_CREDENTIALS,
				},
				{
					label: 'Notification Preferences',
					name: 'notifications',
					href: AppRoutes.LAWYERAPP_SETTINGS_NOTIFICATIONS,
				}
			);
		}

		setOptions(commonOptions);
	}, [userInformation]);

	React.useEffect(() => {
		if (!router.isReady || !options) return;

		const tabParam = tab as string | undefined;

		if (typeof tabParam === 'undefined') {
			const fallbackRoute =
				userInformation?.data?.role === 'client'
					? AppRoutes.CLIENTAPP_SETTINGS_PERSONAL
					: AppRoutes.LAWYERAPP_SETTINGS_PERSONAL;

			router.replace(fallbackRoute);
			return;
		}

		const tabIndex = options.findIndex((option) => option.name === tab);
		setSelectedIndex(tabIndex !== -1 ? tabIndex : 0);
	}, [tab, options, router.isReady, userInformation]);

	const handleRefetch = async (): Promise<void> => {
		await new Promise((resolve) => setTimeout(resolve, 300));
		refetch();
	};

	if (options?.length === 0) {
		return <LoaderScreen />;
	}

	return (
		<div className="mt-[14px] md:mt-0">
			<Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
				<div className="sticky top-0 z-20 bg-gray-15 pt-2 w-full min-h-8 md:min-h-[50px] overflow-x-auto mb-2 md:mb-0">
					<Tab.List
						className={
							'flex items-center gap-5 border-b pb-0 w-max min-w-full h-6 md:h-auto'
						}
						aria-labelledby="Settings Tabs"
					>
						{options?.map((item, index) => (
							<Link
								key={`settings-option-${index}`}
								href={item.href || ''}
								passHref
								aria-labelledby="Settings Tab"
							>
								<Tab
									className={clsx('pb-1 mb:pb-4', {
										'border-b-2 border-purple-150 outline-none ':
											selectedIndex === index,
									})}
								>
									<Typography
										type="semibold-14"
										className={clsx('text-xs sm:text-sm', {
											'text-purple-150': selectedIndex === index,
											'opacity-60': selectedIndex !== index,
										})}
									>
										{item.label}
									</Typography>
								</Tab>
							</Link>
						))}
					</Tab.List>
				</div>
				{selectedIndex !== undefined ? (
					<>
						<Tab.Panels>
							<Tab.Panel>
								<PersonalDetails
									id={String(userInformation?.data?.id ?? 'personal-details')}
									refetch={refetch}
									userInformation={userInformation?.data}
								/>
							</Tab.Panel>
							<Tab.Panel>
								<CredentialsSettings
									refetch={refetch}
									userInformation={userInformation?.data}
								/>
							</Tab.Panel>
							{userInformation?.data.role === 'client' && (
								<Tab.Panel>
									<PaymentDetails
										refetch={handleRefetch}
										userInformation={userInformation?.data}
									/>
								</Tab.Panel>
							)}
							{userInformation?.data.role === 'client' && (
								<Tab.Panel>
									<ClientCommunicationPreferences
										refetch={refetch}
										userInformation={userInformation?.data}
									/>
								</Tab.Panel>
							)}
							{userInformation?.data.role === 'lawyer' && (
								<Tab.Panel>
									<NotificationPreferences
										refetch={refetch}
										userInformation={userInformation?.data}
									/>
								</Tab.Panel>
							)}
						</Tab.Panels>
					</>
				) : null}
			</Tab.Group>
		</div>
	);
};
